import { useEffect, useRef, useCallback, useState } from 'react';

const STORAGE_PREFIX = 'luxy:draft:';
const SAVE_DELAY_MS = 1500;

/**
 * Auto-saves a form's `data` to localStorage every ~1.5s of inactivity.
 *
 * Usage:
 *   const draft = useFormDraft('prospect:new', formData, setFormData, {
 *       initialData: emptyFormData,  // optional, but recommended
 *   });
 *   draft.clear();          // call after a successful submit
 *   draft.hasDraft;         // true if a MEANINGFUL draft was restored on mount
 *
 * On mount, if a draft exists for `key`, it is NOT auto-applied — instead
 * `hasDraft` is set so the parent can show a "Restaurer brouillon ?" prompt.
 *
 * `initialData` (optional): when passed, the hook will :
 *   - never save a snapshot that is byte-for-byte identical to `initialData`
 *     (avoids creating a phantom "empty form" draft as soon as the user
 *     opens the page without typing anything)
 *   - never report `hasDraft=true` if the persisted draft equals
 *     `initialData` (prevents the "Brouillon non envoyé" dialog from
 *     appearing on every fresh form open).
 */
export function useFormDraft(key, data, setData, options = {}) {
    const { initialData } = options;
    const [hasDraft, setHasDraft] = useState(false);
    const [draftSavedAt, setDraftSavedAt] = useState(null);
    const lastSerialized = useRef('');
    const fullKey = `${STORAGE_PREFIX}${key}`;
    const initialSerialized = initialData !== undefined ? JSON.stringify(initialData) : null;

    // On mount: detect existing draft (don't auto-apply).
    useEffect(() => {
        try {
            const raw = localStorage.getItem(fullKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.data && parsed.savedAt) {
                    // Ignore drafts that are identical to the empty/initial
                    // form — they don't represent any user input and would
                    // pop up the "Brouillon non envoyé" dialog needlessly.
                    if (initialSerialized !== null) {
                        try {
                            if (JSON.stringify(parsed.data) === initialSerialized) {
                                localStorage.removeItem(fullKey);
                                return;
                            }
                        } catch { /* fall through */ }
                    }
                    setHasDraft(true);
                    setDraftSavedAt(parsed.savedAt);
                }
            }
        } catch { /* corrupt draft — ignore */ }
        // Intentionally only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced auto-save while typing.
    useEffect(() => {
        if (!data) return;
        const t = setTimeout(() => {
            try {
                const ser = JSON.stringify(data);
                if (ser === lastSerialized.current) return;
                // Skip persistence when the form is still untouched (equals
                // the initial empty state). Avoids creating a phantom draft.
                if (initialSerialized !== null && ser === initialSerialized) {
                    lastSerialized.current = ser;
                    return;
                }
                lastSerialized.current = ser;
                localStorage.setItem(
                    fullKey,
                    JSON.stringify({ data, savedAt: new Date().toISOString() }),
                );
            } catch {
                // QuotaExceeded or serialization error — silent
            }
        }, SAVE_DELAY_MS);
        return () => clearTimeout(t);
    }, [data, fullKey, initialSerialized]);

    const restore = useCallback(() => {
        try {
            const raw = localStorage.getItem(fullKey);
            if (!raw) return false;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.data) {
                setData(parsed.data);
                setHasDraft(false);
                return true;
            }
        } catch { /* ignore */ }
        return false;
    }, [fullKey, setData]);

    const clear = useCallback(() => {
        try { localStorage.removeItem(fullKey); } catch { /* ignore */ }
        setHasDraft(false);
        setDraftSavedAt(null);
        lastSerialized.current = '';
    }, [fullKey]);

    return { hasDraft, draftSavedAt, restore, clear };
}

export default useFormDraft;
